import { RERUN_DEFAULT_ITERATIONS } from "../model/constants"
import type { SofiaProposedAction } from "../model/types"
import { isRecord, readString, readStringArray } from "./actionArgumentReaders"
import { navigateDestinationLabel } from "./actionRoutes"
import {
  AGENT_CONNECTION_LABELS,
  AGENT_CONNECTION_MODEL_TYPES,
  createAgentConnectionType,
} from "./agentActionConfig"
import { summarizeAlertActionArgs } from "./alertActionArgs"
import { summarizeAttributePatch } from "./modifyActionArgs"
import { pythonMetricActionTitle } from "./pythonMetricActionArgs"
import { summarizeCustomDimensions } from "./reportActionArgs"
import { parseRunActionArgs } from "./runActionArgs"
import {
  draftTestCasesMutationLabel,
  draftTestCasesTitle,
  summarizeDraftTestCasesArgs,
  summarizeUpdateTestCasesArgs,
  updateTestCasesMutationLabel,
  updateTestCasesTitle,
} from "./testCaseBatchLabels"
import {
  summarizeBuildDashboardArgs,
  summarizeMetricFilter,
} from "./widgetActionArgs"

function rerunIterations(action: SofiaProposedAction): number {
  const parsed = parseRunActionArgs(action)
  return parsed?.ok === true && parsed.action.kind === "rerun_run"
    ? parsed.action.args.iterations
    : RERUN_DEFAULT_ITERATIONS
}

/** Human title for the card header, e.g. "Re-run run abc123 ×3". */
export function buildActionTitle(action: SofiaProposedAction): string {
  switch (action.kind) {
    case "create_python_metric_draft":
    case "preview_python_metric":
    case "cancel_python_metric_preview":
    case "save_python_analysis_metric":
    case "publish_python_metric":
      return pythonMetricActionTitle(action) ?? "Python metric"
    case "create_alert":
      return `Create alert ${isRecord(action.args.config) ? (readString(action.args.config, "name") ?? "") : ""}`.trim()
    case "update_alert":
      return `Update alert ${readString(action.args, "alert_id") ?? "?"}`
    case "delete_alert":
      return `Delete alert ${readString(action.args, "alert_id") ?? "?"}`
    case "test_alert":
      return `Test alert ${readString(action.args, "alert_id") ?? "?"}`
    case "launch_run": {
      const testSetId = readString(action.args, "test_set_id") ?? "?"
      const iterations = action.args.iterations
      return `Launch run on test set ${testSetId}${typeof iterations === "number" && iterations > 1 ? ` ×${iterations}` : ""}`
    }
    case "launch_selected_run": {
      const parsed = parseRunActionArgs(action)
      if (!parsed?.ok || parsed.action.kind !== "launch_selected_run") {
        return "Launch selected cases"
      }
      const selected = parsed.action.args
      return `Launch ${selected.expected_simulations} simulations (${selected.test_case_ids.length} selected cases ×${selected.iterations})`
    }
    case "rerun_run": {
      const runId = readString(action.args, "run_id") ?? "?"
      return `Re-run run ${runId} ×${rerunIterations(action)}`
    }
    case "rerun_failed": {
      const runId = readString(action.args, "run_id") ?? "?"
      const count = (readStringArray(action.args, "test_case_ids") ?? []).length
      const noun = count === 1 ? "case" : "cases"
      return `Re-run ${count} ${noun} from run ${runId}`
    }
    case "rerun_metrics":
      return "Re-run selected metrics on selected simulations"
    case "cancel_run":
      return `Stop run ${readString(action.args, "run_id") ?? "?"}`
    case "attach_metrics_to_agent": {
      const metricIds = readStringArray(action.args, "metric_ids") ?? []
      const agentId = readString(action.args, "agent_id") ?? "?"
      const noun = metricIds.length === 1 ? "metric" : "metrics"
      return `Attach ${metricIds.length} ${noun} to ${agentId}`
    }
    case "duplicate_persona":
      return `Duplicate persona ${readString(action.args, "persona_id") ?? "?"}`
    case "duplicate_agent":
      return `Duplicate agent ${readString(action.args, "agent_id") ?? "?"}`
    case "duplicate_test_set":
      return `Duplicate test set ${readString(action.args, "test_set_id") ?? "?"}`
    case "create_agent": {
      const name = readString(action.args, "display_name") ?? "?"
      const connectionType =
        createAgentConnectionType(action.args) ?? "inbound_voice"
      return `Connect ${AGENT_CONNECTION_LABELS[connectionType]} agent "${name}"`
    }
    case "create_test_set": {
      const name = readString(action.args, "display_name") ?? "?"
      return `Create test set "${name}"`
    }
    case "add_test_case": {
      const testSetId = readString(action.args, "test_set_id") ?? "?"
      return `Add a test case to ${testSetId}`
    }
    case "draft_test_cases":
      return draftTestCasesTitle(action)
    case "create_persona": {
      const name = readString(action.args, "name") ?? "?"
      return `Create persona "${name}"`
    }
    case "create_metric": {
      const metricName = readString(action.args, "metric_name") ?? "?"
      return `Create metric "${metricName}"`
    }
    case "create_dashboard": {
      const name = readString(action.args, "display_name") ?? "?"
      return `Create dashboard "${name}"`
    }
    case "create_report": {
      const name = readString(action.args, "name") ?? "?"
      return `Create report "${name}"`
    }
    case "create_monitor": {
      const name = readString(action.args, "name") ?? "?"
      return `Create alert "${name}"`
    }
    case "create_widget": {
      const dashboardId = readString(action.args, "dashboard_id") ?? "?"
      const widgetType = readString(action.args, "widget_type")
      const noun =
        widgetType === "text"
          ? "a section header"
          : widgetType === "table"
            ? "a table"
            : "a chart"
      return `Add ${noun} to dashboard ${dashboardId}`
    }
    case "build_dashboard": {
      const name = readString(action.args, "display_name")
      const dashboardId = readString(action.args, "dashboard_id")
      const count = widgetCount(action.args)
      const widgets = `${count} widget${count === 1 ? "" : "s"}`
      return name
        ? `Build dashboard "${name}" with ${widgets}`
        : `Add ${widgets} to dashboard ${dashboardId ?? "?"}`
    }
    case "create_review_project": {
      const name = readString(action.args, "display_name") ?? "?"
      return `Create Human Review project "${name}"`
    }
    case "add_run_to_review_project":
      return `Add run ${readString(action.args, "run_id") ?? "?"} to Human Review`
    case "add_simulations_to_review_project": {
      const count =
        readStringArray(action.args, "simulation_output_ids")?.length ?? 0
      return `Add ${count} simulation${count === 1 ? "" : "s"} to Human Review`
    }
    case "remove_simulations_from_review_project": {
      const count =
        readStringArray(action.args, "simulation_output_ids")?.length ?? 0
      return `Remove ${count} simulation${count === 1 ? "" : "s"} from Human Review`
    }
    case "update_agent":
      return `Update agent ${readString(action.args, "agent_id") ?? "?"}`
    case "delete_agent":
      return `Delete agent ${readString(action.args, "agent_id") ?? "?"}`
    case "update_persona":
      return `Update persona ${readString(action.args, "persona_id") ?? "?"}`
    case "delete_persona":
      return `Delete persona ${readString(action.args, "persona_id") ?? "?"}`
    case "update_metric":
      return `Update metric ${readString(action.args, "metric_id") ?? "?"}`
    case "delete_metric":
      return `Delete metric ${readString(action.args, "metric_id") ?? "?"}`
    case "update_dashboard":
      return `Update dashboard ${readString(action.args, "dashboard_id") ?? "?"}`
    case "delete_dashboard":
      return `Delete dashboard ${readString(action.args, "dashboard_id") ?? "?"}`
    case "update_report":
      return `Update report ${readString(action.args, "report_id") ?? "?"}`
    case "delete_report":
      return `Delete report ${readString(action.args, "report_id") ?? "?"}`
    case "update_monitor":
      return `Update alert ${readString(action.args, "monitor_id") ?? "?"}`
    case "delete_monitor":
      return `Delete alert ${readString(action.args, "monitor_id") ?? "?"}`
    case "update_run": {
      const tags = readStringArray(action.args, "tags") ?? []
      const noun = tags.length === 1 ? "tag" : "tags"
      return tags.length > 0
        ? `Set ${tags.length} ${noun} on run ${readString(action.args, "run_id") ?? "?"}`
        : `Clear tags on run ${readString(action.args, "run_id") ?? "?"}`
    }
    case "update_test_set":
      return `Update test set ${readString(action.args, "test_set_id") ?? "?"}`
    case "update_test_case":
      return `Update test case ${readString(action.args, "test_case_id") ?? "?"}`
    case "update_test_cases":
      return updateTestCasesTitle(action)
    case "delete_test_set":
      return `Delete test set ${readString(action.args, "test_set_id") ?? "?"}`
    case "delete_test_case":
      return `Delete test case ${readString(action.args, "test_case_id") ?? "?"}`
    case "update_review_project":
      return `Update Human Review project ${readString(action.args, "project_id") ?? "?"}`
    case "delete_review_project":
      return `Delete Human Review project ${readString(action.args, "project_id") ?? "?"}`
    case "contact_support":
      return "Draft an email to Coval support with this message"
    case "navigate": {
      const destination = readString(action.args, "destination") ?? "?"
      return `Go to ${navigateDestinationLabel(destination)}`
    }
    case "render_visualization": {
      const title = readString(action.args, "title") ?? "?"
      return `Render chart "${title}"`
    }
  }
}

/**
 * The EXACT client-side mutation an armed card is about to fire — rendered as
 * the armed button label so the user confirms the real call, not a paraphrase.
 */
export function buildMutationLabel(action: SofiaProposedAction): string {
  switch (action.kind) {
    case "create_python_metric_draft":
    case "preview_python_metric":
    case "cancel_python_metric_preview":
    case "save_python_analysis_metric":
    case "publish_python_metric":
      return pythonMetricActionTitle(action) ?? "Python metric"
    case "create_alert":
      return "Create alert"
    case "update_alert":
      return "Update alert"
    case "delete_alert":
      return "Delete alert"
    case "test_alert":
      return "Test alert"
    case "launch_run": {
      const testSetId = readString(action.args, "test_set_id") ?? "?"
      const agentId = readString(action.args, "agent_id") ?? "?"
      return `launchRun(test_set_id: ${testSetId}, agent_id: ${agentId})`
    }
    case "launch_selected_run":
      return buildActionTitle(action)
    case "rerun_run": {
      const runId = readString(action.args, "run_id") ?? "?"
      return `launchRun(config of ${runId}, iteration_count: ${rerunIterations(action)})`
    }
    case "rerun_failed": {
      const runId = readString(action.args, "run_id") ?? "?"
      const count = (readStringArray(action.args, "test_case_ids") ?? []).length
      return `launchRun(config of ${runId}, test_case_ids: ${count} ids)`
    }
    case "rerun_metrics":
      return "Queue metric evaluations"
    case "cancel_run":
      return `deleteRun(${readString(action.args, "run_id") ?? "?"})`
    case "attach_metrics_to_agent": {
      const agentId = readString(action.args, "agent_id") ?? "?"
      const count = (readStringArray(action.args, "metric_ids") ?? []).length
      return `addAgentMetrics(agent_id: ${agentId}, metric_ids: ${count} ids)`
    }
    case "duplicate_persona":
      return `duplicatePersona(${readString(action.args, "persona_id") ?? "?"})`
    case "duplicate_agent":
      return `duplicateAgent(${readString(action.args, "agent_id") ?? "?"})`
    case "duplicate_test_set":
      return `duplicateTestSet(${readString(action.args, "test_set_id") ?? "?"})`
    case "create_agent": {
      const name = readString(action.args, "display_name") ?? "?"
      const connectionType =
        createAgentConnectionType(action.args) ?? "inbound_voice"
      return `createAgent(display_name: ${name}, model_type: ${AGENT_CONNECTION_MODEL_TYPES[connectionType]})`
    }
    case "create_test_set": {
      const name = readString(action.args, "display_name") ?? "?"
      return `createTestSet(display_name: ${name})`
    }
    case "add_test_case": {
      const testSetId = readString(action.args, "test_set_id") ?? "?"
      return `addTestSetRows(test_set_id: ${testSetId}, 1 case)`
    }
    case "draft_test_cases":
      return draftTestCasesMutationLabel(action)
    case "create_persona": {
      const name = readString(action.args, "name") ?? "?"
      return `createPersona(name: ${name})`
    }
    case "create_metric": {
      const metricName = readString(action.args, "metric_name") ?? "?"
      return `createMetric(metric_name: ${metricName})`
    }
    case "create_dashboard": {
      const name = readString(action.args, "display_name") ?? "?"
      return `createDashboard(display_name: ${name})`
    }
    case "create_report": {
      const name = readString(action.args, "name") ?? "?"
      const count = (readStringArray(action.args, "run_ids") ?? []).length
      return `createReport(name: ${name}, ${count} runs)`
    }
    case "create_monitor": {
      const name = readString(action.args, "name") ?? "?"
      return `createAlert(name: ${name})`
    }
    case "create_widget": {
      const dashboardId = readString(action.args, "dashboard_id") ?? "?"
      const metricId = readString(action.args, "metric_id") ?? "?"
      return `createWidget(dashboard_id: ${dashboardId}, metric: ${metricId})`
    }
    case "build_dashboard": {
      const name =
        readString(action.args, "display_name") ??
        readString(action.args, "dashboard_id") ??
        "?"
      return `buildDashboard(${name}, ${widgetCount(action.args)} widgets)`
    }
    case "create_review_project": {
      const name = readString(action.args, "display_name") ?? "?"
      return `createReviewProject(display_name: ${name})`
    }
    case "add_run_to_review_project":
      return `addRunToReviewProject(${readString(action.args, "project_id") ?? "?"}, ${readString(action.args, "run_id") ?? "?"})`
    case "add_simulations_to_review_project":
    case "remove_simulations_from_review_project": {
      const count =
        readStringArray(action.args, "simulation_output_ids")?.length ?? 0
      return `updateReviewProject(${readString(action.args, "project_id") ?? "?"}, ${count} simulation ids)`
    }
    case "update_agent":
      return `updateAgent(${readString(action.args, "agent_id") ?? "?"})`
    case "delete_agent":
      return `deleteAgent(${readString(action.args, "agent_id") ?? "?"})`
    case "update_persona":
      return `updatePersona(${readString(action.args, "persona_id") ?? "?"})`
    case "delete_persona":
      return `deletePersona(${readString(action.args, "persona_id") ?? "?"})`
    case "update_metric":
      return `updateMetric(${readString(action.args, "metric_id") ?? "?"})`
    case "delete_metric":
      return `deleteMetric(${readString(action.args, "metric_id") ?? "?"})`
    case "update_dashboard":
      return `updateDashboard(${readString(action.args, "dashboard_id") ?? "?"})`
    case "delete_dashboard":
      return `deleteDashboard(${readString(action.args, "dashboard_id") ?? "?"})`
    case "update_report":
      return `updateReport(${readString(action.args, "report_id") ?? "?"})`
    case "delete_report":
      return `deleteReport(${readString(action.args, "report_id") ?? "?"})`
    case "update_monitor":
      return `updateAlert(${readString(action.args, "monitor_id") ?? "?"})`
    case "delete_monitor":
      return `deleteAlert(${readString(action.args, "monitor_id") ?? "?"})`
    case "update_run": {
      const tagCount = readStringArray(action.args, "tags")?.length ?? 0
      return `updateRun(${readString(action.args, "run_id") ?? "?"}, ${tagCount} tags)`
    }
    case "update_test_set":
      return `updateTestSet(${readString(action.args, "test_set_id") ?? "?"})`
    case "update_test_case":
      return `updateTestCase(${readString(action.args, "test_case_id") ?? "?"})`
    case "update_test_cases":
      return updateTestCasesMutationLabel(action)
    case "delete_test_set":
      return `deleteTestSet(${readString(action.args, "test_set_id") ?? "?"})`
    case "delete_test_case":
      return `deleteTestCase(${readString(action.args, "test_case_id") ?? "?"})`
    case "update_review_project":
      return `updateReviewProject(${readString(action.args, "project_id") ?? "?"})`
    case "delete_review_project":
      return `deleteReviewProject(${readString(action.args, "project_id") ?? "?"})`
    case "contact_support":
      return "draftSupportEmail(message)"
    case "navigate": {
      const destination = readString(action.args, "destination") ?? "?"
      const resourceId = readString(action.args, "resource_id")
      return resourceId
        ? `navigate(${destination}, ${resourceId})`
        : `navigate(${destination})`
    }
    case "render_visualization": {
      const title = readString(action.args, "title") ?? "?"
      return `renderVisualization(title: ${title})`
    }
  }
}

function widgetCount(args: Record<string, unknown>): number {
  const widgets = args.widgets
  return Array.isArray(widgets) ? widgets.length : 0
}

function displayValue(value: unknown): string {
  if (typeof value === "string") {
    return value
  }
  if (
    typeof value === "number" ||
    typeof value === "boolean" ||
    typeof value === "bigint"
  ) {
    return String(value)
  }
  return JSON.stringify(value) ?? "?"
}

/** Flatten args into display rows for the card's muted summary table. */
export function summarizeActionArgs(
  action: SofiaProposedAction,
): Array<{ key: string; value: string }> {
  if (
    ["create_alert", "update_alert", "delete_alert", "test_alert"].includes(
      action.kind,
    )
  ) {
    return summarizeAlertActionArgs(action)
  }
  if (action.kind === "draft_test_cases") {
    return summarizeDraftTestCasesArgs(action)
  }
  if (action.kind === "update_test_cases") {
    return summarizeUpdateTestCasesArgs(action)
  }
  if (action.kind === "build_dashboard") {
    return summarizeBuildDashboardArgs(action.args)
  }
  return Object.entries(action.args)
    .filter(
      ([key, value]) =>
        value !== undefined &&
        (value !== null || action.kind === "update_agent") &&
        !(
          [
            "create_python_metric_draft",
            "save_python_analysis_metric",
          ].includes(action.kind) && key === "source"
        ),
    )
    .flatMap(([key, value]) => {
      if (key === "custom_dimensions" && action.kind === "create_report") {
        return summarizeCustomDimensions(value)
      }
      if (
        key === "expected_output_json" &&
        action.kind === "update_test_case"
      ) {
        return summarizeAttributePatch(value)
      }
      if (key === "metric_filter" && action.kind === "create_widget") {
        return summarizeMetricFilter(action.args)
      }
      if (value === null) {
        return [{ key, value: "Clear saved value" }]
      }
      if (Array.isArray(value)) {
        const limit =
          pythonMetricActionTitle(action) || action.kind === "rerun_metrics"
            ? value.length
            : 4
        const shown = value.slice(0, limit).map(displayValue).join(", ")
        const more =
          value.length > limit ? `, +${value.length - limit} more` : ""
        return [{ key, value: `${shown}${more}` }]
      }
      return [{ key, value: displayValue(value) }]
    })
}
